import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Row, Col, Typography, Result, Icon, Button,
} from 'antd';
import * as PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import {
  DASHBOARD_GRAPH_HOVER_BORDER_COLOUR,
  DASHBOARD_GRAPH_BAR_COLOUR_GREY,
  DASHBOARD_GRAPH_BAR_FADED_COLOUR_GREY,
  DASHBOARD_GRAPH_BAR_FADED_COLOUR_RED,
  DASHBOARD_GRAPH_BAR_COLOUR_RED,
  DASHBOARD_GRAPH_MAIN_FADED_COLOUR,
  DASHBOARD_GRAPH_MAIN_COLOUR,
} from '../dashboard-util';
import routeLocations from '../../../../utility/route-locations';
import {getDefaultLocale, isPurCoInstall} from "../../../../configWeb";
import {addDaysToJSDate} from "../../bynx/packageUtil";

const { Text } = Typography;


function DashGraphCreditBurn({ history, dashboardData, sessionUser }) {
  const intl = useIntl();

  // Data series
  const [labels, setLabels] = useState([]);
  const [series1, setSeries1] = useState([]);
  const [series2, setSeries2] = useState([]);
  const [series3, setSeries3] = useState([]);
  const [details, setDetails] = useState([]);

  // Graph dataset
  const data = {
    labels,
    datasets: [
      {
        label: intl.formatMessage({ id: 'widgetDataUsed' }),
        backgroundColor: DASHBOARD_GRAPH_BAR_FADED_COLOUR_GREY,
        borderColor: DASHBOARD_GRAPH_BAR_COLOUR_GREY,
        borderWidth: 2,
        hoverBackgroundColor: DASHBOARD_GRAPH_BAR_COLOUR_GREY,
        hoverBorderColor: DASHBOARD_GRAPH_HOVER_BORDER_COLOUR,
        data: series1,
        stack: 'theOne',
      },
      {
        label: intl.formatMessage({ id: 'widgetDataExpiring' }),
        backgroundColor: DASHBOARD_GRAPH_BAR_FADED_COLOUR_RED,
        borderColor: DASHBOARD_GRAPH_BAR_COLOUR_RED,
        borderWidth: 2,
        hoverBackgroundColor: DASHBOARD_GRAPH_BAR_COLOUR_RED,
        hoverBorderColor: DASHBOARD_GRAPH_HOVER_BORDER_COLOUR,
        data: series2,
        stack: 'theOne',
      },
      {
        label: intl.formatMessage({ id: 'widgetDataCreditsAvailable' }),
        backgroundColor: DASHBOARD_GRAPH_MAIN_FADED_COLOUR,
        borderColor: DASHBOARD_GRAPH_MAIN_COLOUR,
        borderWidth: 2,
        hoverBackgroundColor: DASHBOARD_GRAPH_MAIN_COLOUR,
        hoverBorderColor: DASHBOARD_GRAPH_HOVER_BORDER_COLOUR,
        data: series3,
        stack: 'theOne',
      },
    ],
  };

  // Load data (** TAKE ONLY UP TO 3 SETS OF DATA - more than 4 will not display correctly)
  useEffect(() => {
    if (dashboardData && dashboardData.packageSummaryList && dashboardData.packageSummaryList.length > 0) {
      // Today's UTC date
      let todayDate = new Date();
      todayDate = new Date(todayDate.getUTCFullYear(), todayDate.getUTCMonth(), todayDate.getUTCDate());

      // Date options
      const dateDisplayOptions = {
        dateStyle: 'short',
      };

      // Locale adjust
      const userLocale = sessionUser && sessionUser.locale ? sessionUser.locale.replace('_', '-') : getDefaultLocale().locale;

      // Labels
      setLabels(dashboardData.packageSummaryList.filter((row, index) => index < 3).map(row => (isPurCoInstall() ? intl.formatMessage({ id: 'credits' }) : row.packageTitle)));

      // Used
      const usedNo = [];
      setSeries1(dashboardData.packageSummaryList.filter((row, index) => index < 3).map((row, index) => {
        usedNo.push(row.tokensUsed);
        return usedNo[index];
      }));

      // Expiring (still UTC for package expiry!!)
      const expiredNo = [];
      setSeries2(dashboardData.packageSummaryList.filter((row, index) => index < 3).map((row, index) => {
        let theDate = new Date(row.expiryDate);
        theDate = new Date(theDate.getUTCFullYear(), theDate.getUTCMonth(), theDate.getUTCDate());
        theDate = addDaysToJSDate(theDate, -7);
        expiredNo.push(theDate < todayDate ? row.tokensRemaining : 0);
        return expiredNo[index];
      }));

      // Available (still UTC for package expiry!!)
      setSeries3(dashboardData.packageSummaryList.filter((row, index) => index < 3).map((row) => {
        let theDate = new Date(row.expiryDate);
        theDate = new Date(theDate.getUTCFullYear(), theDate.getUTCMonth(), theDate.getUTCDate());
        theDate  = addDaysToJSDate(theDate, -7);
        return theDate >= todayDate ? row.tokensRemaining : 0;
      }));

      // Details
      setDetails(dashboardData.packageSummaryList.filter((row, index) => index < 3).map((row, index, array) => ({
        span: 24 / array.length,
        purchased: new Date(row.purchasedDate).toLocaleDateString(userLocale, dateDisplayOptions),
        expired: new Date(row.expiryDate).toLocaleDateString(userLocale, dateDisplayOptions),
        danger: expiredNo[index] > 0,
        info: usedNo[index] === 0 ? intl.formatMessage({ id: 'widgetNotUsedYet' }) : '',
        secondary: usedNo[index] === 0,
        averageText: row.inspectionsPerWeek === 0 ? intl.formatMessage({ id: 'widgetInspectionsPerWeekNA' }) : `${row.inspectionsPerWeek} ${intl.formatMessage({ id: 'widgetInspectionsPerWeek' })}`,
      })));
    }
  }, [dashboardData, intl, sessionUser]);

  // No packages (in case)
  if (!dashboardData || !dashboardData.packageSummaryList || dashboardData.packageSummaryList.length < 1) {
    return (
      <Result
        icon={<Icon type="tags" theme="twoTone" />}
        title={<FormattedMessage id="widgetMessageCredits" />}
        extra={(
          <Button type="link" onClick={() => { history.push(routeLocations.purchaseCredits); }} style={{ height: '16px', lineHeight: '16px' }}>
            <FormattedMessage id="widgetMessagePurchase" />
          </Button>
        )}
      />
    );
  }

  // GRAPH and INFO
  return (
    <div>
      <Bar
        data={data}
        height={300}
        width={420}
        options={{
          responsive: true,
          maintainAspectRatio: true,
          scales: {
            xAxes: [{
              stacked: true,
            }],
            yAxes: [{
              stacked: true,
              // ticks: {
              //   suggestedMax: maxValue + 50,
              // },
            }],
          },
          legend: {
            onHover(e) {
              e.target.style.cursor = 'pointer';
            },
          },
        }}
      />
      <Row type="flex" justify="center" align="top">
        <Row style={{ fontSize: '12px', marginLeft: '2.7em', width: 390 }}>
          { details.map(record => (
            <Col span={record.span} key={Math.random()}>
              <table align="center">
                <tbody>
                  <tr>
                    <td align="center">
                      <Text>
                        <FormattedMessage id={isPurCoInstall() ? 'allocated' : 'widgetDataPurchased'} />
                        &nbsp;
                        { record.purchased }
                      </Text>
                    </td>
                  </tr>
                  <tr>
                    <td align="center">
                      <Text type={record.danger ? 'danger' : ''}>
                        <FormattedMessage id="widgetDataExpires" />
                        &nbsp;
                        { record.expired }
                      </Text>
                    </td>
                  </tr>
                  <tr><td align="center"><Text type={record.secondary ? 'secondary' : ''}>{ record.secondary ? record.info : record.averageText }</Text></td></tr>
                </tbody>
              </table>
            </Col>
          )) }
        </Row>
      </Row>
    </div>
  );
}

DashGraphCreditBurn.propTypes = {
  history: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  dashboardData: PropTypes.oneOfType([PropTypes.object]),
};

DashGraphCreditBurn.defaultProps = {
  dashboardData: null,
};

export default withRouter(DashGraphCreditBurn);
